import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy } from "@angular/core";
import { Subject } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import { BaseComponent, UserService, SupportService, ProductService, environment } from "@mypxplat/xplat/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CreateTicketComponent } from "../modals/create-ticket/create-ticket.component";
import { AppService, AvoService, SeoService } from "@mypxplat/xplat/web/core";
import { BreadcrumbItem } from "../fender-ui/layout-header/layout-header.component";
import { SimpleSignupComponent } from "../modals/simple-signup/simple-signup.component";
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-support",
  templateUrl: "support.component.html",
  standalone: false,
})
export class SupportComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() showPageTitle: boolean = false;
  public destroy$: Subject<any> = new Subject();
  public displayedSection: string;
  public breadcrumbs: BreadcrumbItem[] = [];

  public openTickets: Array<any>;
  public closedTickets: Array<any>;
  public loadingOpen: boolean = false;
  public loadingClosed: boolean = false;
  public refreshing: boolean = false;
  public supportPath: string = null;
  public description: string;

  constructor(
    userService: UserService,
    public appService: AppService,
    public supportService: SupportService,
    public productService: ProductService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private avoService: AvoService,
    private _seo: SeoService
  ) {
    super(userService);
  }

  ngOnInit() {
    window.scrollTo(0, 0);
    this.displayedSection = this._activatedRoute.snapshot.params["category"] || "options";
    this.setDescription();
    this.setBreadcrumbs();
    this._seo.setPageMeta({
      title: "Support | MyFender — Help with your Fender and PreSonus gear",
      description: "Get help with Fender guitars, amps, and PreSonus recording software and hardware. Browse support options, create a ticket, or check the status of your existing tickets.",
      canonical: "https://my.fender.com/support",
    });
    // Listen to route parameter changes (for browser back/forward navigation)
    this._activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const category = params["category"] || "options";
      if (category !== this.displayedSection) {
        this.switchSection(category, false, true);
      }
      this.setDescription();
      this.setBreadcrumbs();
    });

    this.supportService.openTickets$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.openTickets = result;
      this.loadingOpen = false;
      this.refreshing = false;
    });
    this.supportService.closedTickets$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.closedTickets = result;
      this.loadingClosed = false;
      this.refreshing = false;
    });
    this.switchSection(this.displayedSection);
    if (this.userService.user) {
      this.supportService.getOpenTickets(true);
      this.supportService.getClosedTickets(true);
    }
  }

  setDescription() {
    if (this.displayedSection == "options") {
      this.description = "newdash.fender_support_options_description";
    } else {
      this.description = "newdash.fender_support_recording_description";
    }
  }

  setBreadcrumbs() {
    if (this.displayedSection != "options") {
      this.breadcrumbs = [
        { label: "header.nav.home", routerLink: ["/home"] },
        { label: "fui-header.support", clickHandler: () => this.switchSection("options") },
        { label: "fui-header.recording_software_hardware_support" },
      ];
    } else {
      this.breadcrumbs = [{ label: "header.nav.home", routerLink: ["/home"] }, { label: "fui-header.support" }];
    }
  }

  switchSection(section, force?, skipNavigation?) {
    if (section != "options" && !this.userService.user) {
      this.appService.promptSimpleSignup(SimpleSignupComponent);
      return;
    }
    if (section != this.displayedSection && !skipNavigation) {
      let url = ["support/", section];
      if (section == "options") url = ["support/"];
      this._router.navigate(url);
    }
    this.displayedSection = section;
    this.setDescription();
    this.setBreadcrumbs();
    if (section == "open") {
      this.loadingOpen = true;
      this.supportService.getOpenTickets(force);
    } else if (section == "closed") {
      this.loadingClosed = true;
      this.supportService.getClosedTickets(force);
    }
  }

  goToFenderSupport() {
    window.open("https://support.fender.com/hc/en-us", "_blank");
  }

  refresh() {
    this.refreshing = true;
    if (this.displayedSection == "open" || this.displayedSection == "closed") {
      this.switchSection(this.displayedSection, true);
    }
  }

  createTicket() {
    if (!this.user) {
      this.appService.promptSimpleSignup(SimpleSignupComponent);
      return;
    }
    // Fire CTA Clicked for Create Ticket (only in My Fender)
    if (this.userService.appId === "fender") {
      this.avoService.avoInitted$
        .pipe(
          filter((v) => !!v),
          take(1)
        )
        .subscribe(() => {
          const te = this.avoService.trackEvent();
          if (te && typeof (te as any).ctaClicked === "function") {
            const page = this.avoService.getPageProperties({ pageName: "Support" });
            const extra: Record<string, any> = {
              ctaText: "Create Ticket",
              ctaLocation: "Support",
              utmId: (page as any).utmId ?? 0,
              utmTerm: (page as any).utmTerm ?? "",
            };
            const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
            (te as any).ctaClicked(payload);
          }
        });
    }
    //if (this.user.active) {
    if (this.user.firstName && this.user.lastName) {
      const modalRef = this.appService.showModal(CreateTicketComponent, { size: "lg", ariaLabelledBy: "modal-title" });
      modalRef.result.then(
        (result) => {
          this.refreshing = true;
          this.loadingOpen = true;
          this.switchSection("open", true);
          if (result.ticket_id) {
            this._router.navigate(["/support/ticketdetail/", result.ticket_id]);
          }
        },
        (error) => {}
      );
    } else {
      if (confirm("You need to fill out your first and last name before creating a support ticket.")) {
        this._router.navigate(["/account/general/"]);
      }
    }
    // } else {
    //   alert("Please activate your account before creating a Support Ticket.");
    // }
  }

  goToTicket(ticket) {
    this._router.navigate(["/support/ticketdetail/", ticket.id]);
  }

  goToReturnAuth() {
    this._router.navigate(["/rma"]);
  }
  handleRmaRefresh(event: boolean): void {
    this.refreshing = !event;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
